import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { StatusService } from '../../../services/status.service';
import { Status, ENTITY_TYPES, EntityType } from '../../../models/Status';
import { TableAction } from '../../../shared/components/table/table.component';

@Component({
  selector: 'app-status-management',
  templateUrl: './status-management.component.html',
  styleUrls: ['./status-management.component.css']
})
export class StatusManagementComponent implements OnInit {
  // Dados
  statuses: Status[] = [];
  filteredStatuses: Status[] = [];
  entityTypes = ENTITY_TYPES;
  
  // Configuração da Tabela
  columns = [
    { 
      titulo: 'Nome', 
      label: 'name',
      customClass: () => 'status-name-cell'
    },
    { 
      titulo: 'Setor', 
      label: 'entityType',
      formatter: (value: string) => this.getSectorLabel(value),
      customClass: () => 'status-sector-cell'
    },

  ];

  actions: TableAction[] = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view', visible: true },
    { label: 'Editar', icon: 'fas fa-pen-to-square', action: 'edit', class: 'btn-edit', visible: true },
    { label: 'Excluir', icon: 'fas fa-trash-can', action: 'delete', class: 'btn-delete', visible: true }
  ];
  
  // Filtros
  selectedEntityType: EntityType | 'all' = 'all';
  
  // Estados
  loading: boolean = false;
  error: string = '';
  
  // Modal de sucesso
  showSuccessModal: boolean = false;
  successMessage: string = '';
  
  // Paginação
  currentPage: number = 1;
  pageSize: number = 10;
  totalItems: number = 0;
  totalPages: number = 0;
  

  constructor(
    private statusService: StatusService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadStatuses();
  }

  /**
   * Carrega os status da API
   */
  loadStatuses(): void {
    this.loading = true;
    this.error = '';

    if (this.selectedEntityType !== 'all') {
      // Carregar por entidade
      this.statusService.getByEntity(this.selectedEntityType, false)
        .subscribe({
          next: (data) => {
            this.statuses = data;
            this.applyFilters();
            this.loading = false;
          },
          error: (err) => {
            this.error = 'Erro ao carregar status';
            console.error('Erro ao carregar status:', err);
            this.loading = false;
          }
        });
    } else {
      // Carregar todos paginados
      this.statusService.getPaged(this.currentPage, this.pageSize)
        .subscribe({
          next: (response) => {
            this.statuses = response.data;
            this.totalItems = response.pagination.totalItems;
            this.totalPages = response.pagination.totalPages;
            this.applyFilters();
            this.loading = false;
          },
          error: (err) => {
            this.error = 'Erro ao carregar status';
            console.error('Erro ao carregar status:', err);
            this.loading = false;
          }
        });
    }
  }

  applyFilters(): void {
    this.filteredStatuses = [...this.statuses];
  }

  /**
   * Evento de mudança de filtro de entidade
   */
  onEntityTypeChange(): void {
    this.currentPage = 1;
    this.loadStatuses();
  }

  /**
   * Limpar filtros
   */
  clearFilters(): void {
    this.selectedEntityType = 'all';
    this.currentPage = 1;
    this.loadStatuses();
  }

  /**
   * Gerencia cliques nas ações da tabela
   */
  onActionClick(event: { action: string; item: Status }): void {
    switch (event.action) {
      case 'view':
        this.viewStatus(event.item);
        break;
      case 'edit':
        this.editStatus(event.item);
        break;
      case 'delete':
        this.deleteStatus(event.item);
        break;
    }
  }

  /**
   * Mudança de página
   */
  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadStatuses(); // Sempre recarrega os dados, independente do filtro
  }

  /**
   * Mudança de tamanho de página
   */
  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadStatuses();
  }

  /**
   * Navegar para criação de novo status
   */
  createNew(): void {
    this.router.navigate(['/config/status/new']);
  }

  /**
   * Navegar para edição de status
   */
  editStatus(status: Status): void {
    this.router.navigate(['/config/status', status.id, 'edit']);
  }

  /**
   * Visualizar detalhes do status
   */
  viewStatus(status: Status): void {
    this.router.navigate(['/config/status', status.id, 'view']);
  }



  /**
   * Deletar status
   */
  deleteStatus(status: Status): void {
    if (!status || !status.id) {
      this.error = 'Status não encontrado para deletar';
      return;
    }

    this.loading = true;
    this.error = '';
    
    const statusId = status.id;
    const statusName = status.name;
    
    this.statusService.delete(statusId).subscribe({
      next: (response) => {
        // Backend retorna { success: true, message, note }
        if (response && response.success) {
          // Remover da lista
          this.statuses = this.statuses.filter(s => s.id !== statusId);
          this.applyFilters();
          this.loading = false;
          
          // Mostrar modal de sucesso
          this.successMessage = `Status "${statusName}" excluído com sucesso!`;
          this.showSuccessModal = true;
          
          // Fechar modal após 3 segundos
          setTimeout(() => {
            this.showSuccessModal = false;
          }, 3000);
        } else {
          // Se success = false
          this.error = response?.message || 'Erro ao deletar status';
          this.loading = false;
        }
      },
      error: (err) => {
        this.error = err.error?.message || err.message || 'Erro ao deletar status';
        this.loading = false;
      }
    });
  }

  /**
   * Fechar modal de sucesso
   */
  closeSuccessModal(): void {
    this.showSuccessModal = false;
    this.successMessage = '';
  }

  /**
   * Obter label do setor/entidade
   */
  getSectorLabel(entityType: string): string {
    const entity = this.entityTypes.find(e => e.value === entityType);
    if (entity) {
      return `${entity.module} - ${entity.label}`;
    }
    return entityType;
  }
}
